import type {
  Blueprint,
  CoverageDecision,
  CoverageDecisionInput,
  RtmViewRow,
  ViewStats,
} from '@halilural/rtmkit';
import type {
  AdminConfigItem,
  AdminConfigResponse,
  CreateSkillInput,
  UpdateSkillInput,
  AdminCostsResponse,
  AdminSystemHealthResponse,
  AdminRoadmapDetail,
  AdminRoadmapSummary,
  AdminSkill,
  AdminSkillPage,
  AdminSkillQuery,
  AdminUserDetail,
  AdminUsersResponse,
  BlueprintBacklogInput,
  BlueprintMetaInput,
  BlueprintRuleInput,
  BlueprintStepInput,
  BusinessRule,
  BusinessRuleInput,
  ContentSummary,
  CreateBusinessRuleInput,
  DbQueryInput,
  DbQueryResult,
  ItemConstraint,
  ItemModelInput,
  BackfillCoverageInput,
  CreatePromptTemplateInput,
  DraftPromptTemplateInput,
  DraftPromptTemplateResult,
  DraftQuestion,
  EmailSend,
  EmailTemplateDetail,
  EmailTemplateSummary,
  GenerateQuestionsInput,
  ListEmailSendsResponse,
  LlmInfo,
  PromptTemplateResponse,
  QuestionDifficulty,
  QuestionStatus,
  QuestionType,
  ResetContentInput,
  ResetContentResult,
  SimGraduationLoopInput,
  SimGraduationLoopResult,
  SimRoadmapInput,
  SimRoadmapResult,
  SimUserSummary,
  SendEmailInput,
  SendEmailResponse,
  UpdateDraftQuestionInput,
} from '@prepzio/shared';

import { API_URL } from '../env';

import { authHeaders } from './auth';

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } };

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(await authHeaders()),
      ...(init?.headers ?? {}),
    },
  });

  const body = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (!res.ok || !body || body.ok !== true) {
    const err = body && body.ok === false ? body.error : undefined;
    throw new ApiError(err?.code ?? 'ERROR', err?.message ?? res.statusText, res.status);
  }
  return body.data;
}

export type GenerationUsage = {
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
};

export type GenerateResult = {
  drafts: DraftQuestion[];
  count: number;
  // PRE-169/170: gate outcomes surfaced to the reviewer.
  duplicatesFlagged: number;
  malformedRejected: number;
  usage: GenerationUsage;
};

/**
 * PRE-284 backfill response — a PLAN, not questions.
 *
 * `cells` is what will be generated; nothing is written yet when this returns.
 * Naming it `cellsPlanned` rather than `generated` is the point: the screen that
 * reports it must not claim work the queue has not done.
 */
export type BackfillResult = {
  dryRun: boolean;
  enqueued?: number;
  cellsPlanned?: number;
  jobs?: number;
  floor?: number;
  totalDeficit?: number;
  cells: {
    skillId: string;
    skillTitle: string;
    difficulty: QuestionDifficulty;
    deficit: number;
    steps: { type: QuestionType; count: number }[];
  }[];
};

// PRE-168 coverage-gap report (mirrors apps/api/src/lib/coverage-gap.ts).
export type CoverageGapCell = {
  skillId: string;
  skillSlug: string;
  skillTitle: string;
  difficulty: 'easy' | 'medium' | 'hard';
  published: number;
  /** PRE-286: what this cell should hold — the floor plus what demand bought. */
  target: number;
  deficit: number;
  demand: number;
  priority: number;
};
export type CoverageGapReport = {
  floor: number;
  /** One page of the ranked worklist — see `totalCells` for its real length. */
  gaps: CoverageGapCell[];
  totalDeficit: number;
  skillsBelowFloor: number;
  /** PRE-285: how many cells the worklist holds in total, paging aside. */
  totalCells: number;
  limit: number;
  offset: number;
};

// PRE-261 bank health (mirrors apps/api/src/lib/bank-health.ts).
export type HealthFinding = {
  severity: 'critical' | 'warning';
  kind: 'inverted' | 'over_exposed' | 'no_signal' | 'slow_outlier' | 'fast_outlier' | 'weak';
  message: string;
};
export type BankHealthItem = {
  item: {
    id: string;
    prompt: string;
    status: string;
    difficulty: string;
    skillTitle: string;
    answers: number;
    medianDurationMs: number | null;
    measuredPValue: number | null;
    measuredDiscrimination: number | null;
    measuredResponses: number | null;
    calibrationReviewReason: string | null;
  };
  exposure: number;
  findings: HealthFinding[];
};
export type BankHealthReport = {
  liveItems: number;
  totalAnswers: number;
  medianDurationMs: number | null;
  unhealthy: BankHealthItem[];
  mostExposed: BankHealthItem[];
};

// PRE-293 item models — an expert writes the shape, the algorithm fills it in.
export type ItemModelRow = {
  id: string;
  title: string;
  skillId: string;
  skillTitle: string | null;
  difficulty: QuestionDifficulty;
  type: QuestionType;
  stem: string;
  slots: Record<string, string[]>;
  constraints: ItemConstraint[];
  expectedConcepts: string[];
  status: string;
  /** How many coherent items the model can yield — computed, free. */
  maxYield: number;
  /** How many drafts it has actually produced. A model never run is no model. */
  produced: number;
  createdAt: string;
};
export type ItemModelPreview = {
  problems: string[];
  maxYield: number;
  items: { binding: Record<string, string>; stem: string }[];
};
export type ItemModelGenerateResult = {
  count: number;
  attempted: number;
  failed: number;
  duplicatesFlagged: number;
  malformedRejected: number;
};

// PRE-289 launch readiness (mirrors apps/api/src/lib/launch-readiness.ts).
export type ReadinessCheckKey =
  'skill_coverage' | 'cell_completeness' | 'review_integrity' | 'calibration';
export type ReadinessCheckState = 'pass' | 'blocked' | 'waived' | 'info';
export type LaunchWaiver = {
  id: string;
  checkKey: string;
  reason: string;
  owner: string;
  expiresAt: string;
};
export type ReadinessWorklistItem = {
  checkKey: ReadinessCheckKey;
  label: string;
  deficit: number;
  screen: string | null;
};
export type ReadinessCheck = {
  key: ReadinessCheckKey;
  title: string;
  question: string;
  state: ReadinessCheckState;
  actual: number;
  target: number;
  items: ReadinessWorklistItem[];
  waiver?: LaunchWaiver;
  advisory: boolean;
};
export type LaunchReadinessReport = {
  ready: boolean;
  blockingCount: number;
  checks: ReadinessCheck[];
  worklist: ReadinessWorklistItem[];
  expiredWaivers: LaunchWaiver[];
};
export type LaunchWaiverInput = {
  checkKey: string;
  reason: string;
  owner: string;
  expiresAt: string;
};

// PRE-283 question-flow report (mirrors apps/api/src/lib/question-flow.ts).
export type QuestionFlowStepId =
  | 'ranking'
  | 'generation'
  | 'invariant_gate'
  | 'dedup'
  | 'judge'
  | 'human_review'
  | 'pretest'
  | 'published'
  | 'monitoring';
export type QuestionFlowMetric = { label: string; value: number; of?: number };
export type QuestionFlowStep = {
  id: QuestionFlowStepId;
  title: string;
  what: string;
  /** Items sitting in this step right now; null when nothing can queue here. */
  waiting: number | null;
  /** Items this step turned away; null when it never rejects. */
  rejected: number | null;
  metrics: QuestionFlowMetric[];
  /** The panel route that owns this step, or null when no screen does. */
  screen: string | null;
  issue: string;
};
export type QuestionFlowReport = {
  steps: QuestionFlowStep[];
  bottleneck: QuestionFlowStepId | null;
  rejections: { reason: string; count: number; step: QuestionFlowStepId }[];
  totals: { generated: number; published: number; rejected: number };
};

/** The retirement flow's states. A person moves an item; nothing else does. */
export type QuestionLifecycle = 'published' | 'pretest' | 'under_review' | 'retired';

// PRE-262 review throughput.
export type ReviewDecision = {
  id: string;
  verdict: 'approve' | 'reject';
  reason?: string;
  note?: string;
};
export type ReviewBatchResult = {
  results: { id: string; ok: boolean; status?: string; error?: string }[];
  applied: number;
  skipped: number;
};
export type ReviewStats = {
  pendingReview: number;
  reachedHuman: number;
  judgeBlocked: number;
  /** Null until anything has reached a person. */
  approvalRate: number | null;
  reasons: { reason: string; count: number }[];
};

export const adminApi = {
  /**
   * PRE-155: paged and searchable. The endpoint defaults to 50 rows, so a caller
   * that needs a specific skill searches for it rather than asking for all
   * 2,482 — which is what every caller used to do, at 386 KB a time.
   */
  listSkills: (query: AdminSkillQuery = {}) => {
    const params = new URLSearchParams();
    if (query.q) params.set('q', query.q);
    if (query.origin) params.set('origin', query.origin);
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.offset != null) params.set('offset', String(query.offset));
    const qs = params.toString();
    return apiFetch<AdminSkillPage>(`/v1/admin/skills${qs ? `?${qs}` : ''}`);
  },

  /**
   * BR-90 / PRE-123: the merge queue — skills a learner's CV named that the
   * taxonomy did not have. They are already in use; what waits here is our
   * decision about where they belong.
   */
  listSkillReviewQueue: () =>
    apiFetch<{ items: SkillReviewQueueItem[] }>('/v1/admin/skills/review-queue'),
  mergeSkill: (id: string, targetId: string) =>
    apiFetch<{ targetId: string }>(`/v1/admin/skills/${encodeURIComponent(id)}/merge`, {
      method: 'POST',
      body: JSON.stringify({ targetId }),
    }),
  promoteSkill: (id: string, input: { title?: string; slug?: string; parentId?: string | null }) =>
    apiFetch<unknown>(`/v1/admin/skills/${encodeURIComponent(id)}/promote`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  acceptSkill: (id: string) =>
    apiFetch<unknown>(`/v1/admin/skills/${encodeURIComponent(id)}/accept`, { method: 'POST' }),
  createSkill: (input: CreateSkillInput) =>
    apiFetch<AdminSkill>('/v1/admin/skills', { method: 'POST', body: JSON.stringify(input) }),
  updateSkill: (id: string, input: UpdateSkillInput) =>
    apiFetch<AdminSkill>(`/v1/admin/skills/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteSkill: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/v1/admin/skills/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  // Which model the authoring/generation tier runs (shown in the header).
  getLlmInfo: () => apiFetch<LlmInfo>('/v1/admin/llm-info'),

  // Per-(skill × status) counts for the Content overview.
  getContentSummary: () => apiFetch<ContentSummary>('/v1/admin/content-summary'),

  // Raw SQL console (embedded LibSQL Studio → D1). Single statement → Result;
  // transaction batch → Result[].
  dbQuery: (input: DbQueryInput) =>
    apiFetch<DbQueryResult | DbQueryResult[]>('/v1/admin/db/query', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Roadmap simulation: pick a persona from a real user, run the full pipeline.
  listSimUsers: () => apiFetch<{ items: SimUserSummary[] }>('/v1/admin/sim/users'),
  simulateRoadmap: (input: SimRoadmapInput) =>
    apiFetch<SimRoadmapResult>('/v1/admin/sim/roadmap', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteSimRoadmap: (roadmapId: string) =>
    apiFetch<{ deleted: boolean }>(`/v1/admin/sim/${encodeURIComponent(roadmapId)}`, {
      method: 'DELETE',
    }),
  // PRE-198: simulate the graduation → re-loop cycle over a sim roadmap.
  simulateGraduationLoop: (input: SimGraduationLoopInput) =>
    apiFetch<SimGraduationLoopResult>('/v1/admin/sim/graduation-loop', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // Costs dashboard: LLM spend aggregated live from the llm_calls ledger.
  getCosts: () => apiFetch<AdminCostsResponse>('/v1/admin/costs'),
  getSystemHealth: () => apiFetch<AdminSystemHealthResponse>('/v1/admin/system-health'),

  // Runtime config / feature flags: registry + KV overrides (live-editable).
  listConfig: () => apiFetch<AdminConfigResponse>('/v1/admin/config'),
  updateConfig: (key: string, value: unknown) =>
    apiFetch<AdminConfigItem>(`/v1/admin/config/${encodeURIComponent(key)}`, {
      method: 'PATCH',
      body: JSON.stringify({ value }),
    }),

  // Users explorer: list learners + full per-user detail (profile, sessions, roadmaps).
  listUsers: () => apiFetch<AdminUsersResponse>('/v1/admin/users'),
  getUserDetail: (id: string) =>
    apiFetch<AdminUserDetail>(`/v1/admin/users/${encodeURIComponent(id)}`),
  // BR-85: erase all learner data but keep the user's sign-in (fresh-signup reset).
  resetUserData: (id: string, confirmEmail: string) =>
    apiFetch<{ userId: string; keptSignIn: boolean }>(
      `/v1/admin/users/${encodeURIComponent(id)}/data`,
      { method: 'DELETE', body: JSON.stringify({ confirmEmail }) },
    ),

  // Roadmap explorer: list + full structure (→ Mermaid diagram).
  listRoadmaps: () => apiFetch<{ items: AdminRoadmapSummary[] }>('/v1/admin/roadmaps'),
  getRoadmapDetail: (id: string) =>
    apiFetch<AdminRoadmapDetail>(`/v1/admin/roadmaps/${encodeURIComponent(id)}`),

  // AI-assisted prompt template drafting: brief → full template body.
  draftPromptTemplate: (input: DraftPromptTemplateInput) =>
    apiFetch<DraftPromptTemplateResult>('/v1/admin/prompt-templates/draft', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listTemplates: (name?: string) =>
    apiFetch<{ items: PromptTemplateResponse[] }>(
      `/v1/admin/prompt-templates${name ? `?name=${encodeURIComponent(name)}` : ''}`,
    ),
  createTemplate: (input: CreatePromptTemplateInput) =>
    apiFetch<PromptTemplateResponse>('/v1/admin/prompt-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  activateTemplate: (id: string) =>
    apiFetch<PromptTemplateResponse>(`/v1/admin/prompt-templates/${id}/activate`, {
      method: 'POST',
    }),

  generate: (input: GenerateQuestionsInput) =>
    apiFetch<GenerateResult>('/v1/admin/questions/generate', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * BR-81: discard generated drafts. `published` is not a selectable status on
   * the server, so this can never reach an item a learner was measured on.
   * `dryRun` returns the count that would go, which is what the screen shows
   * before asking.
   */
  resetContent: (input: ResetContentInput) =>
    apiFetch<ResetContentResult>('/v1/admin/questions/reset', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * PRE-284: the panel's generate action — fill one skill's below-floor cells.
   *
   * A producer, not a generator: it returns as soon as the jobs are queued, and
   * the drafts appear in the review queue as the consumer works through them.
   * The screen therefore reports what was PLANNED, never what was written.
   */
  backfillSkill: (input: BackfillCoverageInput) =>
    apiFetch<BackfillResult>('/v1/admin/questions/backfill', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // PRE-168: "what to generate next" worklist for the generate screen.
  listCoverageGaps: (params: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return apiFetch<CoverageGapReport>(`/v1/admin/coverage-gaps${qs ? `?${qs}` : ''}`);
  },

  /** PRE-261: what the bank looks like from the answers it has collected. */
  getBankHealth: () => apiFetch<BankHealthReport>('/v1/admin/bank-health'),

  // PRE-293: item models. Only `generateFromItemModel` costs anything.
  listItemModels: () => apiFetch<{ items: ItemModelRow[] }>('/v1/admin/item-models'),
  previewItemModel: (input: ItemModelInput) =>
    apiFetch<ItemModelPreview>('/v1/admin/item-models/preview', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  createItemModel: (input: ItemModelInput) =>
    apiFetch<ItemModelRow>('/v1/admin/item-models', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  generateFromItemModel: (id: string, count: number) =>
    apiFetch<ItemModelGenerateResult>(`/v1/admin/item-models/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify({ count }),
    }),
  getLaunchReadiness: () => apiFetch<LaunchReadinessReport>('/v1/admin/launch-readiness'),
  createLaunchWaiver: (input: LaunchWaiverInput) =>
    apiFetch<LaunchWaiver & { id: string }>('/v1/admin/launch-readiness/waivers', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  deleteLaunchWaiver: (id: string) =>
    apiFetch<{ id: string }>(`/v1/admin/launch-readiness/waivers/${id}`, { method: 'DELETE' }),

  /** PRE-283: the whole authoring pipeline, in the order an item travels it. */
  getQuestionFlow: () => apiFetch<QuestionFlowReport>('/v1/admin/question-flow'),

  /** PRE-261: a person moves an item along the retirement flow. */
  setQuestionLifecycle: (id: string, status: QuestionLifecycle, note?: string) =>
    apiFetch(`/v1/admin/questions/${id}/lifecycle`, {
      method: 'POST',
      body: JSON.stringify({ status, note }),
    }),

  listDrafts: (
    params: {
      status?: QuestionStatus;
      skillId?: string;
      type?: QuestionType;
      difficulty?: QuestionDifficulty;
      limit?: number;
      offset?: number;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.skillId) q.set('skillId', params.skillId);
    if (params.type) q.set('type', params.type);
    if (params.difficulty) q.set('difficulty', params.difficulty);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.offset != null) q.set('offset', String(params.offset));
    const qs = q.toString();
    return apiFetch<{ items: DraftQuestion[]; total: number; limit: number; offset: number }>(
      `/v1/admin/questions/drafts${qs ? `?${qs}` : ''}`,
    );
  },
  /** PRE-262: one request per review SITTING, not per draft. */
  reviewBatch: (decisions: ReviewDecision[]) =>
    apiFetch<ReviewBatchResult>('/v1/admin/questions/review-batch', {
      method: 'POST',
      body: JSON.stringify({ decisions }),
    }),

  /** PRE-262: is the pipeline pointed the right way. */
  getReviewStats: () => apiFetch<ReviewStats>('/v1/admin/review-stats'),

  // Full detail of a single question (roadmap explorer inspector).
  getQuestion: (id: string) =>
    apiFetch<DraftQuestion>(`/v1/admin/questions/${encodeURIComponent(id)}`),

  updateDraft: (id: string, patch: UpdateDraftQuestionInput) =>
    apiFetch<DraftQuestion>(`/v1/admin/questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  approve: (id: string) =>
    apiFetch<DraftQuestion>(`/v1/admin/questions/${id}/approve`, { method: 'POST' }),
  reject: (id: string) =>
    apiFetch<DraftQuestion>(`/v1/admin/questions/${id}/reject`, { method: 'POST' }),

  listEmailTemplates: () =>
    apiFetch<{ items: EmailTemplateSummary[] }>('/v1/admin/email-templates'),
  getEmailTemplate: (id: string) =>
    apiFetch<EmailTemplateDetail>(`/v1/admin/email-templates/${encodeURIComponent(id)}`),
  sendEmail: (input: SendEmailInput, idempotencyKey: string) =>
    apiFetch<SendEmailResponse>('/v1/admin/emails', {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(input),
    }),
  listEmailSends: (
    params: {
      status?: string;
      to?: string;
      templateId?: string;
      limit?: number;
      cursor?: string;
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.to) q.set('to', params.to);
    if (params.templateId) q.set('templateId', params.templateId);
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return apiFetch<ListEmailSendsResponse>(`/v1/admin/emails${qs ? `?${qs}` : ''}`);
  },

  // PRE-188 traceability matrix. Rows are derived server-side from the blueprint;
  // the only writable field is the operator's coverage verdict.
  getRtm: () => apiFetch<RtmResponse>('/v1/admin/rtm'),
  putRtmCoverage: (id: string, input: CoverageDecisionInput) =>
    apiFetch<CoverageDecision>(`/v1/admin/rtm/${encodeURIComponent(id)}/coverage`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  clearRtmCoverage: (id: string) =>
    apiFetch<{ deleted: boolean }>(`/v1/admin/rtm/${encodeURIComponent(id)}/coverage`, {
      method: 'DELETE',
    }),

  // PRE-190: the product document itself, now served from D1 rather than bundled
  // into this app at build time. Editing a rule or a step is a write here, not a
  // code change and a deploy.
  getBusinessRules: () => apiFetch<BusinessRulesResponse>('/v1/admin/business-rules'),
  createBusinessRule: (input: CreateBusinessRuleInput) =>
    apiFetch<AdminBusinessRule>('/v1/admin/business-rules', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateBusinessRule: (id: string, input: BusinessRuleInput) =>
    apiFetch<AdminBusinessRule>(`/v1/admin/business-rules/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /**
   * PRE-126: record that a person has read this rule and it is right.
   *
   * The only write the panel still makes on a rule. The text travels nowhere
   * with it — the review is stored against the sentence as it stands, so a later
   * deploy that rewrites it makes the review stale rather than silently
   * inherited.
   */
  // PRE-102: the review can also say no. A rejection carries a note or the API
  // refuses it — the same rule the RTM decision has always applied.
  reviewBusinessRule: (
    id: string,
    reviewed: boolean,
    verdict: 'confirmed' | 'rejected' = 'confirmed',
    note?: string,
  ) =>
    apiFetch<AdminBusinessRule>(`/v1/admin/business-rules/${encodeURIComponent(id)}/review`, {
      method: 'PUT',
      body: JSON.stringify({ reviewed, verdict, note }),
    }),

  getBlueprint: () => apiFetch<BlueprintResponse>('/v1/admin/blueprint'),
  updateBlueprintMeta: (input: BlueprintMetaInput) =>
    apiFetch<unknown>('/v1/admin/blueprint/meta', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  updateBlueprintStep: (id: string, input: BlueprintStepInput) =>
    apiFetch<{ id: string }>(`/v1/admin/blueprint/steps/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  updateBlueprintBacklog: (id: string, input: BlueprintBacklogInput) =>
    apiFetch<{ id: string }>(`/v1/admin/blueprint/backlog/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
  /**
   * PRE-81: rules gained code anchors, so they gained an editor. Which line
   * enforces an invariant is a product judgement — the sync can read the code but
   * cannot decide what was promised — so this is typed in, not derived.
   */
  updateBlueprintRule: (id: string, input: BlueprintRuleInput) =>
    apiFetch<{ id: string }>(`/v1/admin/blueprint/rules/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),
};

/** A rule as the API returns it: the document record plus its write attribution. */
export type AdminBusinessRule = BusinessRule & {
  /**
   * PRE-126: the deploy that last applied this text (`deploy:<commit>`), because
   * the panel can no longer edit it. Answering "which commit last changed this
   * rule" is most of why the catalog came back to the repository.
   */
  updatedBy: string | null;
  updatedAt: string;
  /** `human` | `llm_draft`. */
  origin: string;
};

export type BusinessRulesResponse = {
  rules: AdminBusinessRule[];
  /** Distinct areas in document order — the panel's group filter. */
  areas: string[];
};

/**
 * The whole blueprint as the engine sees it, plus the phase ids the engine's own
 * model doesn't carry (it keys phases by order) but the panel needs to address a
 * phase for editing.
 */
export type BlueprintResponse = {
  meta: Blueprint['meta'];
  labels: Blueprint['labels'];
  actors: Blueprint['actors'];
  personas: NonNullable<Blueprint['personas']>;
  overview: Blueprint['overview'] | null;
  phases: (Blueprint['phases'][number] & { id: string })[];
  /**
   * PRE-81: rules carry their own row id and anchors, which the engine's model has
   * no field for. The id is needed to address one for editing; the anchors are what
   * the semantic review reads.
   */
  rules: (Blueprint['rules'][number] & { id: string | null; code: string[]; tests: string[] })[];
  backlog: Blueprint['backlog'];
  slices: Blueprint['slices'];
  extras: Record<string, unknown>;
};

/**
 * The matrix as the panel reads it.
 *
 * PRE-139: one lane. `codeVerdicts` and `staleApprovals` used to travel here too
 * — a model's reading of the anchored code, and a flag for a tick given against
 * a finding that had since changed. Both existed to keep a machine's opinion
 * apart from a person's consent, which stops being necessary once only the
 * person has one.
 */
export type RtmResponse = {
  meta: Blueprint['meta'];
  labels: Blueprint['labels'];
  rows: RtmViewRow[];
  stats: ViewStats;
};

export type { EmailSend };

/** BR-90 / PRE-123: one row of the skill merge queue. */
export type SkillReviewQueueItem = {
  id: string;
  slug: string;
  title: string;
  createdAt: string;
  /** How many learners' CVs named it — the ordering signal. */
  userCount: number;
  questionCount: number;
};
